import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  GiTropicalFish, 
  GiCoral, 
  GiClownfish, 
  GiAnglerFish, 
  GiFlyingTrout,
  GiSharkFin 
} from 'react-icons/gi';

// Fish swimming animations - full screen movement
const swimRight = keyframes`
  0% {
    transform: translateX(-150px) translateY(0px);
  }
  20% {
    transform: translateX(20vw) translateY(-15px);
  }
  40% {
    transform: translateX(40vw) translateY(10px);
  }
  60% {
    transform: translateX(60vw) translateY(-8px);
  }
  80% {
    transform: translateX(80vw) translateY(12px);
  }
  100% {
    transform: translateX(calc(100vw + 150px)) translateY(0px);
  }
`;

const swimLeft = keyframes`
  0% {
    transform: translateX(calc(100vw + 150px)) translateY(0px) scaleX(-1);
  }
  20% {
    transform: translateX(80vw) translateY(12px) scaleX(-1);
  }
  40% {
    transform: translateX(60vw) translateY(-10px) scaleX(-1);
  }
  60% {
    transform: translateX(40vw) translateY(15px) scaleX(-1);
  }
  80% {
    transform: translateX(20vw) translateY(-5px) scaleX(-1);
  }
  100% {
    transform: translateX(-150px) translateY(0px) scaleX(-1);
  }
`;

const swimUpDown = keyframes`
  0% {
    transform: translateX(-100px) translateY(80vh);
  }
  25% {
    transform: translateX(25vw) translateY(20vh) rotate(15deg);
  }
  50% {
    transform: translateX(50vw) translateY(60vh) rotate(-10deg);
  }
  75% {
    transform: translateX(75vw) translateY(30vh) rotate(20deg);
  }
  100% {
    transform: translateX(calc(100vw + 100px)) translateY(70vh) rotate(0deg);
  }
`;

const swimWave = keyframes`
  0% {
    transform: translateX(-120px) translateY(0px) rotate(0deg);
  }
  16% {
    transform: translateX(16vw) translateY(-20px) rotate(10deg);
  }
  33% {
    transform: translateX(33vw) translateY(15px) rotate(-15deg);
  }
  50% {
    transform: translateX(50vw) translateY(-25px) rotate(20deg);
  }
  66% {
    transform: translateX(66vw) translateY(18px) rotate(-10deg);
  }
  83% {
    transform: translateX(83vw) translateY(-12px) rotate(15deg);
  }
  100% {
    transform: translateX(calc(100vw + 120px)) translateY(0px) rotate(0deg);
  }
`;

const swimDiagonal = keyframes`
  0% {
    transform: translateX(-80px) translateY(70vh) rotate(-20deg);
  }
  33% {
    transform: translateX(33vw) translateY(45vh) rotate(10deg);
  }
  66% {
    transform: translateX(66vw) translateY(25vh) rotate(-5deg);
  }
  100% {
    transform: translateX(calc(100vw + 80px)) translateY(10vh) rotate(15deg);
  }
`;

const swimShark = keyframes`
  0% {
    transform: translateX(calc(100vw + 150px)) translateY(0px);
  }
  20% {
    transform: translateX(80vw) translateY(12px);
  }
  40% {
    transform: translateX(60vw) translateY(-10px);
  }
  60% {
    transform: translateX(40vw) translateY(15px);
  }
  80% {
    transform: translateX(20vw) translateY(-5px);
  }
  100% {
    transform: translateX(-150px) translateY(0px);
  }
`;

// Coral swaying animation
const sway = keyframes`
  0%, 100% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(2deg) scale(1.02);
  }
  50% {
    transform: rotate(0deg) scale(1);
  }
  75% {
    transform: rotate(-2deg) scale(0.98);
  }
`;

// Bubble animation
const bubbleFloat = keyframes`
  0% {
    transform: translateY(100vh) translateX(0px) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
    transform: translateY(90vh) translateX(5px) scale(1);
  }
  50% {
    transform: translateY(50vh) translateX(-10px) scale(1.2);
  }
  80% {
    transform: translateY(20vh) translateX(15px) scale(0.8);
  }
  100% {
    transform: translateY(-50px) translateX(0px) scale(0);
    opacity: 0;
  }
`;

// Fish fade-in animation
const fishFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// Styled components
const ReefContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const Fish = styled.div`
  position: absolute;
  font-size: ${props => props.size || '2rem'};
  color: ${props => props.color || '#FFD700'};
  opacity: 0;
  ${props => {
    let animationType;
    switch(props.direction) {
      case 'left':
        animationType = swimLeft;
        break;
      case 'diagonal':
        animationType = swimDiagonal;
        break;
      case 'updown':
        animationType = swimUpDown;
        break;
      case 'wave':
        animationType = swimWave;
        break;
      case 'shark':
        animationType = swimShark;
        break;
      default:
        animationType = swimRight;
    }
    return css`
      animation: ${animationType} ${props.duration || '20s'} linear infinite,
                 ${fishFadeIn} 0.5s ease-in forwards;
      animation-delay: ${props.delay || '0s'};
    `;
  }}
  filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.3));
  top: ${props => props.top || '50%'};
  z-index: 5;
`;

const Coral = styled(GiCoral)`
  position: absolute;
  font-size: ${props => props.size || '3rem'};
  color: ${props => props.color || '#FF6B6B'};
  ${props => css`
    animation: ${sway} ${props.duration || '4s'} ease-in-out infinite;
  `}
  animation-delay: ${props => props.delay || '0s'};
  filter: drop-shadow(2px 2px 6px rgba(0,0,0,0.2));
  bottom: ${props => props.bottom || '0px'};
  left: ${props => props.left || '10%'};
  transform-origin: bottom center;
  z-index: 3;
`;

const Bubble = styled.div`
  position: absolute;
  width: ${props => props.size || '8px'};
  height: ${props => props.size || '8px'};
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  ${props => css`
    animation: ${bubbleFloat} ${props.duration || '8s'} linear infinite;
  `}
  animation-delay: ${props => props.delay || '0s'};
  left: ${props => props.left || '50%'};
  box-shadow: inset 2px 2px 4px rgba(255, 255, 255, 0.4);
  z-index: 6;
`;

const SeaweedStrand = styled.div`
  position: absolute;
  bottom: 0;
  left: ${props => props.left || '5%'};
  width: 4px;
  height: ${props => props.height || '80px'};
  background: linear-gradient(to top, #1B5E20, #2E7D32, #4CAF50, #81C784);
  border-radius: 2px;
  transform-origin: bottom center;
  ${props => css`
    animation: ${sway} ${props.duration || '6s'} ease-in-out infinite;
  `}
  animation-delay: ${props => props.delay || '0s'};
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 8px;
    height: 20px;
    background: #4CAF50;
    border-radius: 50%;
    opacity: 0.8;
  }
`;

const ReefScene = () => {
  return (
    <ReefContainer>
      {/* Swimming Fish - Different types and movements */}
      <Fish color="#FFD700" size="2.5rem" duration="18s" delay="0s" top="30%" direction="right">
        <GiTropicalFish />
      </Fish>
      <Fish color="#FF6B9D" size="1.8rem" duration="25s" delay="3s" top="60%" direction="left">
        <GiClownfish />
      </Fish>
      <Fish color="#4ECDC4" size="2rem" duration="22s" delay="7s" top="45%" direction="wave">
        <GiTropicalFish />
      </Fish>
      <Fish color="#45B7D1" size="1.5rem" duration="20s" delay="12s" top="25%" direction="updown">
        <GiFlyingTrout />
      </Fish>
      <Fish color="#FFA07A" size="2.2rem" duration="28s" delay="15s" top="70%" direction="left">
        <GiAnglerFish />
      </Fish>
      <Fish color="#98D8C8" size="1.6rem" duration="24s" delay="8s" top="35%" direction="diagonal">
        <GiClownfish />
      </Fish>
      <Fish color="#F7DC6F" size="1.9rem" duration="26s" delay="18s" top="55%" direction="wave">
        <GiTropicalFish />
      </Fish>
      <Fish color="#87CEEB" size="3rem" duration="35s" delay="25s" top="40%" direction="shark">
        <GiSharkFin />
      </Fish>
      <Fish color="#DDA0DD" size="1.4rem" duration="19s" delay="30s" top="65%" direction="updown">
        <GiClownfish />
      </Fish>
      <Fish color="#20B2AA" size="2.1rem" duration="23s" delay="10s" top="20%" direction="wave">
        <GiFlyingTrout />
      </Fish>
      
      {/* Corals */}
      <Coral color="#FF6B6B" size="4rem" duration="5s" delay="0s" bottom="0px" left="8%" />
      <Coral color="#FF8A80" size="3rem" duration="6s" delay="1s" bottom="0px" left="25%" />
      <Coral color="#FFAB91" size="3.5rem" duration="4.5s" delay="2s" bottom="0px" left="45%" />
      <Coral color="#FF7043" size="2.8rem" duration="5.5s" delay="1.5s" bottom="0px" left="65%" />
      <Coral color="#FF5722" size="3.2rem" duration="4.8s" delay="3s" bottom="0px" left="85%" />
      <Coral color="#D32F2F" size="2.5rem" duration="6.2s" delay="0.5s" bottom="0px" left="15%" />
      
      {/* Seaweed */}
      <SeaweedStrand left="12%" height="60px" duration="7s" delay="0s" />
      <SeaweedStrand left="32%" height="45px" duration="8s" delay="2s" />
      <SeaweedStrand left="68%" height="70px" duration="6.5s" delay="4s" />
      <SeaweedStrand left="88%" height="55px" duration="7.5s" delay="1s" />
      
      {/* Bubbles */}
      <Bubble size="6px" duration="12s" delay="0s" left="20%" />
      <Bubble size="8px" duration="15s" delay="3s" left="40%" />
      <Bubble size="5px" duration="10s" delay="6s" left="60%" />
      <Bubble size="7px" duration="13s" delay="9s" left="80%" />
      <Bubble size="4px" duration="11s" delay="12s" left="30%" />
      <Bubble size="9px" duration="14s" delay="15s" left="70%" />
      <Bubble size="6px" duration="16s" delay="18s" left="15%" />
      <Bubble size="5px" duration="12s" delay="21s" left="85%" />
    </ReefContainer>
  );
};

export default ReefScene;
